import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X, Download, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * In-app modal for previewing a document's PDF without navigating away.
 *
 * Uses Radix Dialog primitives directly (rather than a hand-rolled
 * fixed-position div) so that when this modal opens *inside* the parent
 * customer-detail Sheet (also a Radix Dialog), Radix's nested-dialog
 * support correctly:
 *   - delegates the focus trap from Sheet → DocumentPreviewModal
 *   - restores Sheet focus on close
 *   - keeps pointer events flowing into the new top dialog
 *   - stacks portals so the modal paints above the Sheet
 *
 * The previous implementation used `createPortal(modal, document.body)`
 * which produced a body-level sibling that Radix Sheet's focus trap
 * marked `aria-hidden`/`pointer-events:none` — making the modal visible
 * but completely non-interactive (no clicks, no close, no buttons).
 *
 * Caller passes a `pdfSrc` — either a `data:application/pdf;base64,...`
 * URI (for stored/embedded PDFs) or a plain `/api/...` URL (for
 * server-rendered PDFs).
 */
export default function DocumentPreviewModal({ title, pdfSrc, onClose, onDownload, onEmail }) {
  return (
    <DialogPrimitive.Root open={true} onOpenChange={(open) => { if (!open) onClose?.(); }}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className="fixed inset-0 z-[60] bg-black/70 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
          data-testid="doc-preview-overlay"
        />
        <DialogPrimitive.Content
          className="fixed left-[50%] top-[50%] z-[60] translate-x-[-50%] translate-y-[-50%] bg-white rounded-lg shadow-2xl w-[900px] max-w-[95vw] h-[90vh] flex flex-col focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95"
          data-testid="doc-preview-modal"
          // Stop the parent Sheet's pointer-down-outside listener from
          // intercepting clicks meant for our overlay/buttons.
          onPointerDownOutside={(e) => e.preventDefault()}
        >
          <DialogPrimitive.Title className="sr-only">{title || "Document Preview"}</DialogPrimitive.Title>
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b bg-slate-50 rounded-t-lg shrink-0">
            <div className="text-sm font-semibold text-slate-700 truncate" data-testid="doc-preview-title">{title || "Document Preview"}</div>
            <div className="flex items-center gap-1">
              {onDownload && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onDownload(); }} data-testid="doc-preview-download">
                  <Download className="w-3.5 h-3.5 mr-1" /> Download
                </Button>
              )}
              {onEmail && (
                <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={(e) => { e.stopPropagation(); onEmail(); }} data-testid="doc-preview-email">
                  <Mail className="w-3.5 h-3.5 mr-1" /> Email
                </Button>
              )}
              <DialogPrimitive.Close asChild>
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid="doc-preview-close" aria-label="Close">
                  <X className="w-4 h-4" />
                </Button>
              </DialogPrimitive.Close>
            </div>
          </div>
          {/* PDF iframe */}
          <div className="flex-1 bg-slate-100 overflow-hidden">
            {pdfSrc ? (
              <iframe title="Document preview" src={pdfSrc} className="w-full h-full border-0" data-testid="doc-preview-iframe" />
            ) : (
              <div className="flex items-center justify-center h-full text-sm text-slate-400">No PDF available</div>
            )}
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
